<template>
  <Analytics />
  <GlobalLoader :visible="loaderVisible" />

  <template v-if="!loaderVisible && !hideLayout">
    <MenuModal
      :visible="isOpen"
      :isHomePage="isHomePage"
      @close="toggleOpen(false)"
    />

    <div
      class="flex flex-col w-full min-h-screen text-black bg-white dark:bg-zinc-950 dark:text-white"
    >
      <!-- Header -->
      <header
        class="flex items-center justify-between w-full p-4 min-h-[5rem] md:min-h-[6rem] z-50"
      >
        <RouterLink to="/">
          <template v-if="$colorMode.preference === 'light'">
            <NuxtImg
              :placeholder="[50, 25, 75, 5]"
              :src="
                isOpen && !isHomePage
                  ? '/images/logos/HighfieldLogos/svg/highfield_long_white.svg'
                  : '/images/logos/HighfieldLogos/svg/highfield_long_black.svg'
              "
              loading="lazy"
              class="w-56"
            />
          </template>
          <template v-else>
            <NuxtImg
              :placeholder="[50, 25, 75, 5]"
              src="/images/logos/HighfieldLogos/svg/highfield_long_white.svg"
              class="w-56"
            />
          </template>
        </RouterLink>
        <div class="flex gap-2">
          <LanguageSelector />
          <ColorPicker />

          <button
            @click="toggleOpen()"
            class="relative flex flex-col items-center justify-center w-10 h-10 space-y-2 cursor-pointer hover:animate-pulse"
          >
            <div
              class="w-8 h-1 transition-all duration-300 bg-black dark:bg-white"
              :class="{ 'rotate-45 translate-y-2': isOpen }"
            ></div>
            <div
              class="w-8 h-1 transition-all duration-300 bg-black dark:bg-white"
              :class="{ 'opacity-0': isOpen }"
            ></div>
            <div
              class="w-8 h-1 transition-all duration-300 bg-black dark:bg-white"
              :class="{ '-rotate-45 translate-y-[-8px]': isOpen }"
            ></div>
          </button>
        </div>
      </header>

      <!-- Main Content -->
      <div class="flex items-center justify-center flex-grow w-full h-full">
        <NuxtPage />
      </div>
    </div>

    <HighFieldFooter />
  </template>

  <!-- Render pages that hide layout without header/footer -->
  <template v-if="!loaderVisible && hideLayout">
    <NuxtPage />
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useCookie } from "#app"; // Nuxt composable
import MenuModal from "@/components/MenuModal.vue";
import GlobalLoader from "@/components/GlobalLoader.vue";
import { i18nReady } from "@/composables/i18nReady";
import { useFooterLinks } from "@/composables/useFooterLinks";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Analytics } from "@vercel/analytics/nuxt";

// --- STATE & ROUTES ---
const route = useRoute();
const { locale, setLocale } = useI18n();

// Menu modal
const isOpen = ref(false);
const isHomePage = computed(() => route.path === "/");

// Check if current page should hide layout (header/footer)
const hideLayout = computed(() => {
  const shouldHide =
    route.meta.hideLayout === true ||
    route.path.startsWith("/error") ||
    route.matched.some((m) => m.meta?.hideLayout === true);

  console.log("[app] hideLayout:", shouldHide, "path:", route.path);
  return shouldHide;
});

// Get links from Supabase
const { footerLinks } = useFooterLinks();

// expose i18nReady to template
const i18nReadyRef = i18nReady;

// Loader visibility: show until i18n is ready and a minimum time elapsed
const minLoaderMs = 300;
const loaderVisible = ref(!i18nReadyRef.value);

watch(
  i18nReadyRef,
  (ready) => {
    if (ready) {
      // garantir tempo minimo para evitar flicker
      setTimeout(() => {
        loaderVisible.value = false;
      }, minLoaderMs);
    } else {
      loaderVisible.value = true;
    }
  },
  { immediate: true },
);

// Toggle menu
const toggleOpen = (value?: boolean) => {
  isOpen.value = typeof value === "boolean" ? value : !isOpen.value;
  console.log("[app] menu toggled, isOpen:", isOpen.value);
};

// --- PAGE TITLE ---
const title = ref("Loading...");
useHead({
  title: () => `${title.value} - espera aí!`,
});

// --- LOCALE COOKIE & WATCH ---
const localeCookie = useCookie("i18n_redirected");
const currentLocale = ref(localeCookie.value || "pt");

// Watch changes safely
watch(
  currentLocale,
  async (newLocale) => {
    try {
      console.log("Changing locale:", newLocale);
      await setLocale(newLocale);
      localeCookie.value = newLocale;
    } catch (err) {
      console.error("[nuxt] Failed to change locale:", err);
    }
  },
  { immediate: false },
);

// --- OG IMAGE COMPONENT ---
try {
  defineOgImageComponent("CategoriesOG", {
    logo: "/images/logos/HighfieldLogos/svg/highfield_long_white.svg",
  });
} catch (err) {
  console.error("[nuxt] Failed to define OG component:", err);
}

// --- MOUNTED LOGIC ---
onMounted(() => {
  try {
    // Watch route changes and close menu
    watch(
      () => route.path,
      () => {
        isOpen.value = false;
      },
    );
  } catch (err) {
    console.error("[nuxt] Error during onMounted:", err);
  }
});

// --- SAFE ASYNC FUNCTION EXAMPLE ---
async function fetchServices() {
  try {
    const res = await $fetch("/categories/services");
    return res;
  } catch (err) {
    console.error("[nuxt] Failed to fetch services:", err);
    return null;
  }
}

// --- GLOBAL ERROR HANDLER (opcional) ---
if (process.client) {
  window.addEventListener("error", (event) => {
    console.error("[global] uncaught error:", event.error);
  });
  window.addEventListener("unhandledrejection", (event) => {
    console.error("[global] unhandled promise rejection:", event.reason);
  });
}
</script>
